import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReservationScreen.module.scss';
import { useToasts } from 'react-toast-notifications';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import ScreenContainer from 'common/components/screenContainer/ScreenContainer';
import CompactPageLayout from 'common/components/compactPageLayout/CompactPageLayout';
import compactPageLayoutStyles from 'common/components/compactPageLayout/CompactPageLayout.module.scss';
import InfoCard from 'common/components/infoCard/InfoCard';
import { VehicleReservationViewModel } from 'api/vehicleReservations/models/VehicleReservationViewModel';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, MAX_PAGINATION_ITEMS_PER_PAGE, TIME_FORMAT_DEFAULT } from 'Config';
import Logger from 'common/services/Logger';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import DropdownItem from 'common/components/popover/PopoverItem';
import { FaAngleDown, FaAngleRight, FaCalendarAlt, FaChevronLeft, FaChevronRight, FaEllipsisV, FaInfoCircle, FaStream } from 'react-icons/fa';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import Popover from 'common/components/popover/Popover';
import VehicleReservationsService from 'api/vehicleReservations/VehicleReservationsService';
import DateFormat from 'common/components/dateFormat/dateFormat';
import colors from 'styles/export/colors.module.scss';
import ReservationFilters, { Filters } from './components/filters/ReservationFilters';
import { VehicleReservationsSearchCriteria } from 'api/vehicleReservations/models/VehicleReservationsSearchCriteria';
import { VehicleReservationDto } from 'api/vehicleReservations/models/VehicleReservationDto';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Loading from 'common/services/Loading';
import { useParams, useLocation } from 'react-router-dom';
import { ReservationsVehicleListItemDto } from 'api/vehicles/models/ReservationsVehicleListItemDto';
import { ReservationTripItemViewModel } from 'api/vehicleReservations/models/ReservationTripItemViewModel';
import dayjs, { Dayjs } from 'dayjs';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import moment from 'moment';
import IconWithText from 'common/components/iconWithText/IconWithText';
import CalendarIcon from 'assets/svg/calendar.svg';
import ClockIcon from 'assets/svg/desktop_i_clock.svg';
import GaugeIcon from 'assets/svg/gauge.svg';
import InfoIcon from 'assets/svg/i_info_primaryDark.svg';
import PlayIcon from 'assets/svg/play2.svg';
import HourGlassIcon from 'assets/svg/hour-glass_white.svg';
import FlagBWIcon from 'assets/svg/flag_bw.svg';
import { useHistory } from 'react-router-dom';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, ExpandableRowConfig, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import Tabs, { TabItem } from 'common/components/tabs/Tabs';
import { exportToExcel, ExportColumn } from 'common/utils/exportToExcel';
import Badge from 'common/components/badge/Badge';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import NewRequestScreen from './components/newRequest/NewRequestScreen';
import { getReservationStatusBadgePreset, getReservationStatusLabel } from 'common/utils/reservationStatus';
import CalendarView from './components/calendarView/CalendarView';
import TimelineView, { TimelineRange } from './components/calendarView/TimelineView';
import CalendarFilters, { CalendarFiltersModel } from './components/filters/CalendarFilters';

const itemsPerPage = DEFAULT_PAGINATION_ITEMS_PER_PAGE;

interface FlattenedReservationRow {
    vehicleRegistrationNumber: string;
    vehicleBrandName: string;
    vehicleModelName: string;
    startDate: string;
    endDate: string;
    driverName: string;
    driverEmail: string;
    reason: string;
    availability: string;
}

const ReservationScreen = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);
    const requestsExportRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const { itemId, type } = useParams<{ itemId: string, type: string }>();
    const location = useLocation();
    const [isSearch, search] = useState(type === 'search');

    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);

    const [vehicles, setVehicles] = useState<ReservationsVehicleListItemDto[]>([]);

    const [openRows, setOpenRows] = useState<string[]>([]);
    const [openSubRows, setOpenSubRows] = useState<string[]>([]);
    const [openActionsDropdown, setOpenActionsDropdown] = useState<string | null>(null);

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<VehicleReservationDto | null>(null);
    const [parentItemToRemove, setParentItemToRemove] = useState<ReservationsVehicleListItemDto | null>(null);

    const [showCancelApprovalModal, setShowCancelApprovalModal] = useState<boolean>(false);
    const [itemToCancelApproval, setItemToCancelApproval] = useState<VehicleReservationDto | null>(null);

    const [criteria, setCriteria] = useState<VehicleReservationsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1
    });

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);

    const [order, setOrder] = useState<OrderData<ReservationsVehicleListItemDto>>({
        field: 'vehicleRegistrationNumber' as keyof ReservationsVehicleListItemDto,
        orderColumn: 'v.registration_number',
        isOrderAsc: true,
    });

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(userProfile?.policies ?? [], ['SETTINGUP_RESERVATIONS_WRITE'], 'OR');
    const canEditAll = UsersService.hasPolicies(userProfile?.policies ?? [], ['SETTINGUP_RESERVATIONS_EDIT_ALL'], 'OR');
    const canApprove = UsersService.hasPolicies(userProfile?.policies ?? [], ['SETTINGUP_RESERVATIONS_APPROVE'], 'OR');
    const hasWorkflow = userProfile?.companyReservationType != null && userProfile.companyReservationType !== 'DIRECT';
    dayjs.locale(userProfile?.languageCode ?? 'fr');

    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    const initialTab = tabParam === 'requests' ? 'requests' : tabParam === 'reservations' ? 'reservations' : 'calendars';
    const shouldRefresh = queryParams.get('refresh') === 'true';
    const [activeTab, setActiveTab] = useState<'reservations' | 'requests' | 'calendars'>(initialTab);

    // --- Calendar / Timeline view state (only used in Calendários tab) ---
    const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
    const [viewMode, setViewMode] = useState<'month' | 'timeline'>('month');
    const [calendarMonth, setCalendarMonth] = useState(dayjs().startOf('month'));
    const [calendarSelectedDate, setCalendarSelectedDate] = useState<Dayjs | null>(dayjs());
    const [calendarReservations, setCalendarReservations] = useState<VehicleReservationDto[]>([]);
    const [calendarRequests, setCalendarRequests] = useState<VehicleReservationDto[]>([]);
    const [calendarIsLoading, setCalendarIsLoading] = useState(false);
    const [timelineStart, setTimelineStart] = useState(() => {
        return dayjs().subtract(1, 'day').startOf('day');
    });
    const [timelineRange, setTimelineRange] = useState<TimelineRange>(7);

    useEffect(() => {
        const today = dayjs().startOf('day');
        const rangeEnd = timelineStart.add(timelineRange - 1, 'day');
        if (today.isBefore(timelineStart) || today.isAfter(rangeEnd)) {
            setTimelineStart(today.subtract(1, 'day'));
        }
    }, [timelineRange]);

    const [timelineReservations, setTimelineReservations] = useState<VehicleReservationDto[]>([]);
    const [timelineRequests, setTimelineRequests] = useState<VehicleReservationDto[]>([]);
    const [timelineIsLoading, setTimelineIsLoading] = useState(false);
    const [allVehicles, setAllVehicles] = useState<ReservationsVehicleListItemDto[]>([]);

    // --- Date navigation (shared between month and timeline) ---
    const dateNavTitle = useMemo(() => {
        if (viewMode === 'month') return calendarMonth.format('MMMM YYYY');
        if (viewMode === 'timeline') {
            const rangeEnd = timelineStart.add(timelineRange - 1, 'day');
            return `${timelineStart.format('D MMM')} - ${rangeEnd.format('D MMM YYYY')}`;
        }
        return '';
    }, [viewMode, calendarMonth, timelineStart, timelineRange]);

    const handleDatePrev = useCallback(() => {
        if (viewMode === 'month') {
            setCalendarMonth(prev => {
                const newMonth = prev.subtract(1, 'month');
                setCalendarSelectedDate(current => {
                    if (!current) return newMonth.date(1);
                    const day = Math.min(current.date(), newMonth.daysInMonth());
                    return newMonth.date(day);
                });
                return newMonth;
            });
        } else if (viewMode === 'timeline') setTimelineStart(prev => prev.subtract(timelineRange, 'day'));
    }, [viewMode, timelineRange]);

    const handleDateNext = useCallback(() => {
        if (viewMode === 'month') {
            setCalendarMonth(prev => {
                const newMonth = prev.add(1, 'month');
                setCalendarSelectedDate(current => {
                    if (!current) return newMonth.date(1);
                    const day = Math.min(current.date(), newMonth.daysInMonth());
                    return newMonth.date(day);
                });
                return newMonth;
            });
        } else if (viewMode === 'timeline') setTimelineStart(prev => prev.add(timelineRange, 'day'));
    }, [viewMode, timelineRange]);

    const handleDateToday = useCallback(() => {
        const now = dayjs();
        if (viewMode === 'month') {
            setCalendarMonth(now.startOf('month'));
            setCalendarSelectedDate(now);
        } else if (viewMode === 'timeline') {
            setTimelineStart(now.subtract(1, 'day').startOf('day'));
        }
    }, [viewMode]);

    // --- Requests tab state ---
    const [requests, setRequests] = useState<VehicleReservationDto[]>([]);
    const [requestsIsLoadingMore, setRequestsIsLoadingMore] = useState(false);
    const requestsIsLoadingMoreRef = useRef(false);
    const [requestsPageLoaded, setRequestsPageLoaded] = useState(false);
    const [requestsReachedEnd, setRequestsReachedEnd] = useState(false);
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
    const [requestCounts, setRequestCounts] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
    const [cachedRequestsFilters] = useState<Filters>(() => {
        try {
            const cached = localStorage.getItem('REQUESTSFILTER');
            if (cached) {
                const f = JSON.parse(cached);
                if (f.reservationStartDate || f.reservationEndDate || f.requestDateStart || f.requestDateEnd || f.driverId || f.registrationNumber || (f.status && f.status.length > 0)) {
                    return f;
                }
            }
        } catch { /* ignore */ }
        return null;
    });
    const [requestsCriteria, setRequestsCriteria] = useState<VehicleReservationsSearchCriteria>(() => {
        const base: VehicleReservationsSearchCriteria = { itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE, page: 1 };
        if (cachedRequestsFilters) {
            return {
                ...base,
                startDate: cachedRequestsFilters.reservationStartDate,
                endDate: cachedRequestsFilters.reservationEndDate,
                requestDateStart: cachedRequestsFilters.requestDateStart,
                requestDateEnd: cachedRequestsFilters.requestDateEnd,
                driverId: cachedRequestsFilters.driverId,
                registrationNumber: cachedRequestsFilters.registrationNumber,
                status: cachedRequestsFilters.status,
            };
        }
        return base;
    });
    const [openRequestActionsDropdown, setOpenRequestActionsDropdown] = useState<string | null>(null);
    const [requestsOrder, setRequestsOrder] = useState<OrderData<VehicleReservationDto>>({
        field: 'startDate' as keyof VehicleReservationDto,
        orderColumn: 'vr.start_date',
        isOrderAsc: false,
    });

    // --- Requests filters state ---
    const [requestsFilters, setRequestsFilters] = useState<Filters>(() => cachedRequestsFilters || {});
    const [requestsFiltersTotal, setRequestsFiltersTotal] = useState(() => {
        if (!cachedRequestsFilters) return 0;
        const f = cachedRequestsFilters;
        let total = 0;
        if (f.reservationStartDate) total++;
        if (f.reservationEndDate) total++;
        if (f.requestDateStart) total++;
        if (f.requestDateEnd) total++;
        if (f.driverId) total++;
        if (f.registrationNumber) total++;
        if (f.status && f.status.length > 0) total++;
        return total;
    });

    // --- Calendar filters state ---
    const [calendarFilters, setCalendarFilters] = useState<CalendarFiltersModel>({});
    const [calendarFiltersTotal, setCalendarFiltersTotal] = useState(0);

    const {
        loadedPageRange,
        isLoadingPrevious,
        setLoadedPageRange,
        resetScrollState,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = useBidirectionalScroll({
        cacheKey: 'RESERVATIONSFILTER',
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    // --- Filters ---
    const loadFilters = () => {
        const cachedFilters = localStorage.getItem('RESERVATIONSFILTER');
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true); }
        else { getData(); }
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        localStorage.setItem('RESERVATIONSFILTER', JSON.stringify(f));
    };

    const updateTotalFilters = (f: Filters) => {
        let total = 0;
        if (f.availabilityStartDate) total++;
        if (f.availabilityEndDate) total++;
        if (f.reservationStartDate) total++;
        if (f.reservationEndDate) total++;
        if (f.driverId) total++;
        if (f.registrationNumber) total++;
        setFiltersTotal(total);
    };

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);

        if (!isOnLoad) {
            setVehicles([]);
            resetScrollState();
            setTotalItems(0);
            setOpenRows([]);
        }

        const newCriteria: VehicleReservationsSearchCriteria = {
            ...criteria,
            startDate: f.availabilityStartDate,
            endDate: f.availabilityEndDate,
            reservationStartDate: f.reservationStartDate,
            reservationEndDate: f.reservationEndDate,
            driverId: f.driverId,
            registrationNumber: f.registrationNumber,
            orderBy: f.orderBy || criteria.orderBy,
            orderColumn: f.orderColumn || criteria.orderColumn,
            page: 1,
        };

        if (f.orderBy && f.orderColumn) {
            const getFieldFromOrderColumn = (oc: string): keyof ReservationsVehicleListItemDto => {
                const mapping: Record<string, keyof ReservationsVehicleListItemDto> = {
                    'v.registration_number': 'vehicleRegistrationNumber',
                    'vb.name': 'vehicleBrandName',
                    'vm.name': 'vehicleModelName',
                };
                return mapping[oc] ?? 'vehicleRegistrationNumber';
            };

            setOrder({
                field: getFieldFromOrderColumn(f.orderColumn),
                orderColumn: f.orderColumn,
                isOrderAsc: f.orderBy === 'asc',
            });
        }

        saveFiltersOnCache(f);
        setCriteria(newCriteria);
    };

    const updateFilters = (updates: { key: string; value: any }[]) => {
        const newFilters = { ...filters };
        updates.forEach(u => {
            (newFilters as any)[u.key] = u.value;
        });
        onChangeFilters(newFilters);
    };

    // --- Requests filters functions ---
    const updateRequestsFiltersTotal = (f: Filters) => {
        let total = 0;
        if (f.reservationStartDate) total++;
        if (f.reservationEndDate) total++;
        if (f.requestDateStart) total++;
        if (f.requestDateEnd) total++;
        if (f.driverId) total++;
        if (f.registrationNumber) total++;
        if (f.status && f.status.length > 0) total++;
        setRequestsFiltersTotal(total);
    };

    const saveRequestsFiltersOnCache = (f: Filters) => {
        localStorage.setItem('REQUESTSFILTER', JSON.stringify(f));
    };

    const onChangeRequestsFilters = (f: Filters) => {
        updateRequestsFiltersTotal(f);
        setRequestsFilters(f);
        saveRequestsFiltersOnCache(f);

        setRequests([]);
        setRequestsReachedEnd(false);
        setRequestsPageLoaded(false);

        const newCriteria: VehicleReservationsSearchCriteria = {
            ...requestsCriteria,
            startDate: f.reservationStartDate,
            endDate: f.reservationEndDate,
            requestDateStart: f.requestDateStart,
            requestDateEnd: f.requestDateEnd,
            driverId: f.driverId,
            registrationNumber: f.registrationNumber,
            status: f.status,
            page: 1,
        };

        setRequestsCriteria(newCriteria);
    };

    const updateRequestsFilters = (updates: { key: string; value: any }[]) => {
        const newFilters = { ...requestsFilters };
        updates.forEach(u => {
            (newFilters as any)[u.key] = u.value;
        });
        onChangeRequestsFilters(newFilters);
    };

    // --- Calendar filters functions ---
    const updateCalendarFiltersTotal = (f: CalendarFiltersModel) => {
        let total = 0;
        if (f.brandId) total++;
        if (f.modelId) total++;
        if (f.segmentId) total++;
        if (f.registrationNumber) total++;
        if (f.localIds?.length) total++;
        if (f.driverId) total++;
        setCalendarFiltersTotal(total);
    };

    const onChangeCalendarFilters = (f: CalendarFiltersModel) => {
        updateCalendarFiltersTotal(f);
        setCalendarFilters(f);
    };

    const updateCalendarFilters = (updates: { key: string; value: any }[]) => {
        const newFilters = { ...calendarFilters };
        updates.forEach(u => {
            (newFilters as any)[u.key] = u.value;
        });
        onChangeCalendarFilters(newFilters);
    };

    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.availabilityStartDate || filters.availabilityEndDate) {
            const value = filters.availabilityStartDate && filters.availabilityEndDate
                ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.availabilityStartDate} /> {t('common.range.to').toLowerCase()} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.availabilityEndDate} /></>
                : filters.availabilityStartDate
                    ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.availabilityStartDate} /></>
                    : <>{t('common.range.to')} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.availabilityEndDate!} /></>;
            items.push({
                key: 'availabilityDates',
                label: t('reservations.filters.search_availability') as string,
                value,
                onRemove: () => updateFilters([{ key: 'availabilityStartDate', value: undefined }, { key: 'availabilityEndDate', value: undefined }]),
            });
        }

        if (filters.reservationStartDate || filters.reservationEndDate) {
            const value = filters.reservationStartDate && filters.reservationEndDate
                ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.reservationStartDate} /> {t('common.range.to').toLowerCase()} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.reservationEndDate} /></>
                : filters.reservationStartDate
                    ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.reservationStartDate} /></>
                    : <>{t('common.range.to')} <DateFormat format={DATE_FORMAT_DEFAUT} value={filters.reservationEndDate!} /></>;
            items.push({
                key: 'reservationDates',
                label: t('reservations.filters.search_reservations') as string,
                value,
                onRemove: () => updateFilters([{ key: 'reservationStartDate', value: undefined }, { key: 'reservationEndDate', value: undefined }]),
            });
        }

        if (filters.driverId && filters.driverName) {
            items.push({
                key: 'driver',
                label: t('reservations.filters.driver') as string,
                value: filters.driverName,
                onRemove: () => updateFilters([{ key: 'driverId', value: undefined }, { key: 'driverName', value: undefined }]),
            });
        }

        if (filters.registrationNumber) {
            items.push({
                key: 'registrationNumber',
                label: t('reservations.filters.registration') as string,
                value: filters.registrationNumber,
                onRemove: () => updateFilters([{ key: 'registrationNumber', value: undefined }]),
            });
        }

        return items;
    }, [filters, t]);

    const requestsFilterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (requestsFilters.reservationStartDate || requestsFilters.reservationEndDate) {
            const value = requestsFilters.reservationStartDate && requestsFilters.reservationEndDate
                ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.reservationStartDate} /> {t('common.range.to').toLowerCase()} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.reservationEndDate} /></>
                : requestsFilters.reservationStartDate
                    ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.reservationStartDate} /></>
                    : <>{t('common.range.to')} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.reservationEndDate!} /></>;
            items.push({
                key: 'dates',
                label: `${t('reservations.filters.date_start')} / ${t('reservations.filters.date_end')}` as string,
                value,
                onRemove: () => updateRequestsFilters([{ key: 'reservationStartDate', value: undefined }, { key: 'reservationEndDate', value: undefined }]),
            });
        }

        if (requestsFilters.requestDateStart || requestsFilters.requestDateEnd) {
            const value = requestsFilters.requestDateStart && requestsFilters.requestDateEnd
                ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.requestDateStart} /> {t('common.range.to').toLowerCase()} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.requestDateEnd} /></>
                : requestsFilters.requestDateStart
                    ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.requestDateStart} /></>
                    : <>{t('common.range.to')} <DateFormat format={DATE_FORMAT_DEFAUT} value={requestsFilters.requestDateEnd!} /></>;
            items.push({
                key: 'requestDates',
                label: t('reservations.list.request_date') as string,
                value,
                onRemove: () => updateRequestsFilters([{ key: 'requestDateStart', value: undefined }, { key: 'requestDateEnd', value: undefined }]),
            });
        }

        if (requestsFilters.driverId && requestsFilters.driverName) {
            items.push({
                key: 'driver',
                label: t('reservations.filters.driver') as string,
                value: requestsFilters.driverName,
                onRemove: () => updateRequestsFilters([{ key: 'driverId', value: undefined }, { key: 'driverName', value: undefined }]),
            });
        }

        if (requestsFilters.registrationNumber) {
            items.push({
                key: 'registrationNumber',
                label: t('reservations.filters.registration') as string,
                value: requestsFilters.registrationNumber,
                onRemove: () => updateRequestsFilters([{ key: 'registrationNumber', value: undefined }]),
            });
        }

        if (requestsFilters.status) {
            const statusLabels: Record<string, string> = {
                PENDING_APPROVAL: t('reservations.filters.status_pending') as string,
                APPROVED: t('reservations.filters.status_approved') as string,
                REJECTED: t('reservations.filters.status_rejected') as string,
            };
            items.push({
                key: 'status',
                label: t('reservations.filters.status') as string,
                value: statusLabels[requestsFilters.status] || requestsFilters.status,
                onRemove: () => updateRequestsFilters([{ key: 'status', value: undefined }]),
            });
        }

        return items;
    }, [requestsFilters, t]);

    const calendarFilterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (calendarFilters.brandId && calendarFilters.brandName) {
            items.push({
                key: 'brand',
                label: t('reservations.filters.brand') as string,
                value: calendarFilters.brandName,
                onRemove: () => updateCalendarFilters([{ key: 'brandId', value: undefined }, { key: 'brandName', value: undefined }, { key: 'modelId', value: undefined }, { key: 'modelName', value: undefined }]),
            });
        }

        if (calendarFilters.modelId && calendarFilters.modelName) {
            items.push({
                key: 'model',
                label: t('reservations.filters.model') as string,
                value: calendarFilters.modelName,
                onRemove: () => updateCalendarFilters([{ key: 'modelId', value: undefined }, { key: 'modelName', value: undefined }]),
            });
        }

        if (calendarFilters.segmentId && calendarFilters.segmentName) {
            items.push({
                key: 'segment',
                label: t('reservations.filters.segment') as string,
                value: calendarFilters.segmentName,
                onRemove: () => updateCalendarFilters([{ key: 'segmentId', value: undefined }, { key: 'segmentName', value: undefined }]),
            });
        }

        if (calendarFilters.registrationNumber) {
            items.push({
                key: 'registration',
                label: t('reservations.filters.registration') as string,
                value: calendarFilters.registrationNumber,
                onRemove: () => updateCalendarFilters([{ key: 'registrationNumber', value: undefined }]),
            });
        }

        if (calendarFilters.localIds?.length && calendarFilters.localNames?.length) {
            const operatorLabel = calendarFilters.localOperator === 'AND'
                ? t('common.filter_all' as any)
                : t('common.filter_any' as any);
            items.push({
                key: 'local',
                label: t('reservations.filters.agency') as string,
                value: `(${operatorLabel}): ${calendarFilters.localNames.join(', ')}`,
                onRemove: () => updateCalendarFilters([
                    { key: 'localIds', value: [] },
                    { key: 'localNames', value: [] },
                    { key: 'localOperator', value: 'OR' }
                ]),
            });
        }

        if (calendarFilters.driverId && calendarFilters.driverName) {
            items.push({
                key: 'driver',
                label: t('reservations.filters.driver') as string,
                value: calendarFilters.driverName,
                onRemove: () => updateCalendarFilters([{ key: 'driverId', value: undefined }, { key: 'driverName', value: undefined }]),
            });
        }

        return items;
    }, [calendarFilters, t]);

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    // --- Data loading ---
    const getData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            const vehiclesDB = await VehicleReservationsService.getVehicles(criteria);
            setTotalItems(vehiclesDB.totalItems);

            if (isLoadMore) {
                setVehicles(prev => [...prev, ...vehiclesDB.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setVehicles(vehiclesDB.items);
            }

            Loading.hide();
            setIsLoadingMore(false);

            if (type === 'search' && vehiclesDB.totalItems === 1 && !isLoadMore) {
                toggleRowOpen(vehiclesDB.items[0], true);
            }
        } catch (error) {
            Loading.hide();
            setIsLoadingMore(false);
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        // Determine if this is a "load more" operation or a fresh load
        const isLoadMore = criteria.page > loadedPageRange.max && pageLoaded;
        if (pageLoaded) {
            getData(isLoadMore);
        } else {
            setPageLoaded(true);
            loadFilters();
        }
    }, [criteria]);

    useEffect(() => {
        setReservationFilters();
    }, [search]);

    const setReservationFilters = async () => {
        if (itemId && type === 'search') {
            const reservation = await VehicleReservationsService.getById(itemId);
            setFilters({
                reservationStartDate: reservation.startDate,
                reservationEndDate: reservation.endDate
            });
        }
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || vehicles.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, vehicles.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;
        // For now, reservations doesn't need bidirectional scroll (no scroll restoration)
    }, [isLoadingPrevious, loadedPageRange.min]);
    
    const fetchAllForExport = useCallback(async () => {
        const result = await VehicleReservationsService.getVehicles({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    const handleCustomExport = useCallback(async ({ hiddenColumns, orderedFields }: { hiddenColumns: string[]; orderedFields: (keyof ReservationsVehicleListItemDto)[] }) => {
        Loading.show();
        try {
            const vehiclesResult = await VehicleReservationsService.getVehicles({
                ...criteria,
                page: 1,
                itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
            });

            const flattenedRows: FlattenedReservationRow[] = [];

            for (const vehicle of vehiclesResult.items) {
                const reservationsResult = await VehicleReservationsService.getListByVehicleId({
                    itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                    page: 1,
                    orderBy: 'desc',
                    orderColumn: 'vr.start_date',
                    vehicleId: vehicle.id,
                    registrationNumber: criteria.registrationNumber,
                    brandId: criteria.brandId,
                    modelId: criteria.modelId,
                    segmentId: criteria.segmentId,
                    startDate: criteria.startDate,
                    endDate: criteria.endDate,
                });

                const vehicleAvailability = getAvailabilityText(vehicle);
                if (reservationsResult.items.length === 0) {
                    // Vehicle with no reservations — still include it with availability info
                    flattenedRows.push({
                        vehicleRegistrationNumber: vehicle.vehicleRegistrationNumber ?? '',
                        vehicleBrandName: vehicle.vehicleBrandName ?? '',
                        vehicleModelName: vehicle.vehicleModelName ?? '',
                        startDate: '',
                        endDate: '',
                        driverName: '',
                        driverEmail: '',
                        reason: '',
                        availability: vehicleAvailability,
                    });
                } else {
                    for (const reservation of reservationsResult.items) {
                        flattenedRows.push({
                            vehicleRegistrationNumber: vehicle.vehicleRegistrationNumber ?? '',
                            vehicleBrandName: vehicle.vehicleBrandName ?? '',
                            vehicleModelName: vehicle.vehicleModelName ?? '',
                            startDate: reservation.startDate ? dayjs(reservation.startDate).format(`${DATE_FORMAT_DEFAUT} HH:mm`) : '',
                            endDate: reservation.endDate ? dayjs(reservation.endDate).format(`${DATE_FORMAT_DEFAUT} HH:mm`) : '',
                            driverName: reservation.driverName ?? '',
                            driverEmail: reservation.driverEmail ?? '',
                            reason: reservation.reason ?? '',
                            availability: vehicleAvailability,
                        });
                    }
                }
            }

            // Map each table column to its related export columns
            // Export-only fields (startDate, endDate, driverEmail) are grouped with their logical parent
            const columnExportMap: Record<string, ExportColumn<FlattenedReservationRow>[]> = {
                vehicleRegistrationNumber: [
                    { field: 'vehicleRegistrationNumber', name: t('reservations.list.vehicle') as string },
                ],
                vehicleBrandName: [
                    { field: 'vehicleBrandName', name: t('reservations.brand') as string },
                ],
                vehicleModelName: [
                    { field: 'vehicleModelName', name: t('reservations.model') as string },
                ],
                driverName: [
                    { field: 'startDate', name: t('reservations.list.start_date') as string },
                    { field: 'endDate', name: t('reservations.list.end_date') as string },
                    { field: 'driverName', name: t('reservations.list.driver') as string },
                    { field: 'driverEmail', name: t('reservations.list.driver_email') as string },
                ],
                reason: [
                    { field: 'reason', name: t('reservations.list.reason') as string },
                ],
                availability: [
                    { field: 'availability', name: t('reservations.list.availability') as string },
                ],
            };

            // Build ordered export columns respecting visibility and ordering from sidebar
            const exportColumns: ExportColumn<FlattenedReservationRow>[] = [];
            for (const field of orderedFields) {
                if (hiddenColumns.includes(field)) continue;
                const cols = columnExportMap[field as string];
                if (cols) exportColumns.push(...cols);
            }

            exportToExcel(flattenedRows, exportColumns, t('reservations.title') as string);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't export reservations`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }, [criteria, t]);

    // --- Requests tab export ---
    const fetchAllRequestsForExport = useCallback(async () => {
        const fetchFn = canApprove
            ? VehicleReservationsService.getAllRequests
            : VehicleReservationsService.getMyRequests;
        const result = await fetchFn({
            ...requestsCriteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [requestsCriteria, canApprove]);

    const handleRequestsCustomExport = useCallback(async ({ hiddenColumns, orderedFields }: { hiddenColumns: string[]; orderedFields: (keyof VehicleReservationDto)[] }) => {
        Loading.show();
        try {
            const fetchFn = canApprove
                ? VehicleReservationsService.getAllRequests
                : VehicleReservationsService.getMyRequests;
            const allRequests = await fetchFn({
                ...requestsCriteria,
                page: 1,
                itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
            });

            interface FlatRequestRow {
                startDate: string;
                endDate: string;
                vehicleRegistrationNumber: string;
                vehicleBrandName: string;
                vehicleModelName: string;
                driverName: string;
                driverEmail: string;
                localName: string;
                reason: string;
                status: string;
            }

            const rows: FlatRequestRow[] = allRequests.items.map(r => ({
                startDate: r.startDate ? dayjs(r.startDate).format(`${DATE_FORMAT_DEFAUT} HH:mm`) : '',
                endDate: r.endDate ? dayjs(r.endDate).format(`${DATE_FORMAT_DEFAUT} HH:mm`) : '',
                vehicleRegistrationNumber: r.vehicleRegistrationNumber || r.requestedVehicleRegistrationNumber || '',
                vehicleBrandName: r.vehicleBrandName || r.requestedVehicleBrandName || '',
                vehicleModelName: r.vehicleModelName || r.requestedVehicleModelName || '',
                driverName: r.driverName ?? '',
                driverEmail: r.driverEmail ?? '',
                localName: r.localName ?? '',
                reason: r.reason ?? '',
                status: getReservationStatusLabel(r.status, t),
            }));

            const columnExportMap: Record<string, ExportColumn<FlatRequestRow>[]> = {
                startDate: [
                    { field: 'startDate', name: t('reservations.list.start_date') as string },
                ],
                endDate: [
                    { field: 'endDate', name: t('reservations.list.end_date') as string },
                ],
                vehicleRegistrationNumber: [
                    { field: 'vehicleRegistrationNumber', name: t('reservations.list.vehicle') as string },
                ],
                vehicleBrandName: [
                    { field: 'vehicleBrandName', name: t('reservations.brand') as string },
                ],
                vehicleModelName: [
                    { field: 'vehicleModelName', name: t('reservations.model') as string },
                ],
                driverName: [
                    { field: 'driverName', name: t('reservations.list.driver') as string },
                    { field: 'driverEmail', name: t('reservations.list.driver_email') as string },
                ],
                localName: [
                    { field: 'localName', name: t('reservations.list.agency') as string },
                ],
                reason: [
                    { field: 'reason', name: t('reservations.destination_reason') as string },
                ],
                status: [
                    { field: 'status', name: t('reservations.list.status') as string },
                ],
            };

            const exportColumns: ExportColumn<FlatRequestRow>[] = [];
            for (const field of orderedFields) {
                if (hiddenColumns.includes(field)) continue;
                const cols = columnExportMap[field as string];
                if (cols) exportColumns.push(...cols);
            }

            exportToExcel(rows, exportColumns, t('reservations.tabs.requests') as string);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't export requests`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }, [requestsCriteria, canApprove, t]);

    // --- Requests tab data loading ---
    const getRequestsData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setRequestsIsLoadingMore(true);
                requestsIsLoadingMoreRef.current = true;
            }

            const fetchFn = canApprove
                ? VehicleReservationsService.getAllRequests
                : VehicleReservationsService.getMyRequests;

            const result = await fetchFn(requestsCriteria);

            if (isLoadMore) {
                setRequests(prev => {
                    const newList = [...prev, ...result.items];
                    if (result.items.length === 0 || newList.length >= result.totalItems) {
                        setRequestsReachedEnd(true);
                    }
                    return newList;
                });
            } else {
                setRequests(result.items);
                setRequestsReachedEnd(result.items.length >= result.totalItems);
            }

            Loading.hide();
            setRequestsIsLoadingMore(false);
            requestsIsLoadingMoreRef.current = false;
        } catch (error) {
            Loading.hide();
            setRequestsIsLoadingMore(false);
            requestsIsLoadingMoreRef.current = false;
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get requests list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        if (activeTab === 'requests' && hasWorkflow) {
            const isLoadMore = requestsCriteria.page > 1 && requestsPageLoaded;
            if (requestsPageLoaded) {
                getRequestsData(isLoadMore);
            } else {
                setRequestsPageLoaded(true);
                getRequestsData();
            }
        }
    }, [requestsCriteria]);

    // Refresh requests when switching to the requests tab
    useEffect(() => {
        if (activeTab === 'requests' && hasWorkflow && requestsPageLoaded) {
            setRequests([]);
            setRequestsReachedEnd(false);
            setRequestsCriteria(prev => ({ ...prev, page: 1 }));
        }
    }, [activeTab]);

    // Load request counts for counters and tab badge
    const loadRequestCounts = useCallback(async () => {
        if (!hasWorkflow) {
            setPendingRequestsCount(0);
            setRequestCounts({ total: 0, approved: 0, pending: 0, rejected: 0 });
            return;
        }
        try {
            const fetchFn = canApprove
                ? (c: VehicleReservationsSearchCriteria) => VehicleReservationsService.getAllRequests(c)
                : (c: VehicleReservationsSearchCriteria) => VehicleReservationsService.getMyRequests(c);
            const [totalRes, approvedRes, pendingRes, rejectedRes] = await Promise.all([
                fetchFn({ itemsPerPage: 1, page: 1 }),
                fetchFn({ itemsPerPage: 1, page: 1, status: 'APPROVED' }),
                fetchFn({ itemsPerPage: 1, page: 1, status: 'PENDING_APPROVAL' }),
                fetchFn({ itemsPerPage: 1, page: 1, status: 'REJECTED' }),
            ]);
            setRequestCounts({
                total: totalRes.totalItems,
                approved: approvedRes.totalItems,
                pending: pendingRes.totalItems,
                rejected: rejectedRes.totalItems,
            });
            setPendingRequestsCount(pendingRes.totalItems);
        } catch {
            // Silently fail - counters are not critical
        }
    }, [hasWorkflow, canApprove]);

    useEffect(() => {
        loadRequestCounts();
    }, [loadRequestCounts]);

    // Load requests on mount to populate the tab counter
    useEffect(() => {
        if (hasWorkflow && activeTab !== 'requests') {
            getRequestsData();
            setRequestsPageLoaded(true);
        }
    }, [hasWorkflow]);

    // --- Calendar data loading (Calendários tab) ---
    const calendarApiFilters = useMemo(() => ({
        brandId: calendarFilters.brandId || undefined,
        modelId: calendarFilters.modelId || undefined,
        segmentId: calendarFilters.segmentId || undefined,
        registrationNumber: calendarFilters.registrationNumber || undefined,
        driverId: calendarFilters.driverId || undefined,
        localIds: calendarFilters.localIds?.length ? calendarFilters.localIds : undefined,
        localOperator: calendarFilters.localOperator || undefined,
    }), [calendarFilters.brandId, calendarFilters.modelId, calendarFilters.segmentId, calendarFilters.registrationNumber, calendarFilters.driverId, calendarFilters.localIds, calendarFilters.localOperator]);

    const hasVehicleFilters = !!(calendarFilters.brandId || calendarFilters.modelId || calendarFilters.segmentId || calendarFilters.registrationNumber);

    const getCalendarData = useCallback(async () => {
        if (activeTab !== 'calendars' || viewMode !== 'month') return;

        setCalendarIsLoading(true);
        try {
            const monthStart = calendarMonth.startOf('month').toDate();
            const monthEnd = calendarMonth.endOf('month').toDate();

            // Fetch reservations
            const reservationsPromise = VehicleReservationsService.getListByVehicleId({
                itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                page: 1,
                startDate: monthStart,
                endDate: monthEnd,
                ...calendarApiFilters,
            });

            // Fetch pending requests (if workflow enabled)
            const requestsPromise = hasWorkflow
                ? (canApprove
                    ? VehicleReservationsService.getAllRequests
                    : VehicleReservationsService.getMyRequests)({
                    itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                    page: 1,
                    startDate: monthStart,
                    endDate: monthEnd,
                    status: 'PENDING_APPROVAL',
                    ...calendarApiFilters,
                })
                : Promise.resolve({ items: [] as VehicleReservationDto[] });

            const [reservationsResult, requestsResult] = await Promise.all([reservationsPromise, requestsPromise]);
            setCalendarReservations(reservationsResult.items);
            setCalendarRequests(requestsResult.items);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get calendar data`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setCalendarIsLoading(false);
        }
    }, [activeTab, viewMode, calendarMonth, hasWorkflow, canApprove, calendarApiFilters, calendarRefreshKey]);

    useEffect(() => {
        if (activeTab === 'calendars' && viewMode === 'month') {
            getCalendarData();
        }
    }, [getCalendarData]);

    // --- Timeline data loading (Calendários tab) ---
    const getTimelineData = useCallback(async () => {
        if (activeTab !== 'calendars' || viewMode !== 'timeline') return;

        setTimelineIsLoading(true);
        try {
            const weekStart = timelineStart.toDate();
            const weekEnd = timelineStart.add(timelineRange - 1, 'day').endOf('day').toDate();

            // Fetch reservations
            const reservationsPromise = VehicleReservationsService.getListByVehicleId({
                itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                page: 1,
                startDate: weekStart,
                endDate: weekEnd,
                ...calendarApiFilters,
            });

            // Fetch pending requests (if workflow enabled)
            const requestsPromise = hasWorkflow
                ? (canApprove
                    ? VehicleReservationsService.getAllRequests
                    : VehicleReservationsService.getMyRequests)({
                    itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                    page: 1,
                    startDate: weekStart,
                    endDate: weekEnd,
                    status: 'PENDING_APPROVAL',
                    ...calendarApiFilters,
                })
                : Promise.resolve({ items: [] as VehicleReservationDto[] });

            // Fetch all vehicles (for timeline rows) — always show all, even without reservations
            const vehiclesPromise = VehicleReservationsService.getVehicles({
                itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                page: 1,
                ...calendarApiFilters,
            });

            const [reservationsResult, requestsResult, vehiclesResult] = await Promise.all([reservationsPromise, requestsPromise, vehiclesPromise]);
            setTimelineReservations(reservationsResult.items);
            setTimelineRequests(requestsResult.items);
            setAllVehicles(vehiclesResult.items);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get timeline data`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setTimelineIsLoading(false);
        }
    }, [activeTab, viewMode, timelineStart, timelineRange, hasWorkflow, canApprove, calendarApiFilters, calendarRefreshKey]);

    useEffect(() => {
        if (activeTab === 'calendars' && viewMode === 'timeline') {
            getTimelineData();
        }
    }, [getTimelineData]);

    // Combined items for Calendários tab
    const calendarCombinedItems = useMemo(() => [...calendarReservations, ...calendarRequests], [calendarReservations, calendarRequests]);
    const timelineCombinedItems = useMemo(() => [...timelineReservations, ...timelineRequests], [timelineReservations, timelineRequests]);

    // Counter stats for Calendários tab
    const calendarCounters = useMemo(() => {
        const counterReservations = viewMode === 'month' ? calendarReservations : timelineReservations;
        const counterRequests = viewMode === 'month' ? calendarRequests : timelineRequests;
        const now = dayjs();
        const endOfTomorrow = now.add(1, 'day').endOf('day');

        return {
            totalReservations: counterReservations.length,
            totalRequests: counterRequests.length,
            happeningNow: counterReservations.filter(r =>
                dayjs(r.startDate).isBefore(now) && dayjs(r.endDate).isAfter(now)
            ).length,
            upcoming: counterReservations.filter(r => {
                const start = dayjs(r.startDate);
                return start.isAfter(now) && start.isBefore(endOfTomorrow);
            }).length,
        };
    }, [viewMode, calendarReservations, calendarRequests, timelineReservations, timelineRequests]);

    // Handle refresh when returning from create/edit
    useEffect(() => {
        if (shouldRefresh) {
            // Clean the URL
            const cleanUrl = activeTab === 'requests' ? '/reservations?tab=requests' : activeTab === 'reservations' ? '/reservations?tab=reservations' : '/reservations';
            history.replace(cleanUrl);
            // Reset and reload requests data
            setRequests([]);
            setRequestsReachedEnd(false);
            setRequestsPageLoaded(false);
            setRequestsCriteria(prev => ({ ...prev, page: 1 }));
            loadRequestCounts();
            // Reset and reload reservations data
            setOpenRows([]);
            setOpenSubRows([]);
            setVehicles([]);
            setPageLoaded(false);
            setCriteria(prev => ({ ...prev, page: 1 }));
            // Force calendar/timeline data reload
            setCalendarRefreshKey(prev => prev + 1);
        }
    }, [shouldRefresh]);

    const loadMoreRequests = useCallback(() => {
        if (requestsIsLoadingMoreRef.current || requestsReachedEnd) return;
        setRequestsCriteria(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }));
    }, [requestsReachedEnd]);

    const renderRequestActionsCell = (row: VehicleReservationDto) => {
        const elem = document.getElementById('root');

        return (
            <div style={{ paddingRight: '1rem', position: 'relative' }}>
                <Popover
                    positions={['left', 'bottom']}
                    align={'start'}
                    reposition
                    containerParent={elem ? elem : document.getElementsByTagName('body')[0]}
                    isOpen={openRequestActionsDropdown === row.id}
                    onClickOutside={() => setOpenRequestActionsDropdown(null)}
                    content={() => {
                        const isOwner = row.userCreatedId === userProfile?.id;
                        const isPending = row.status === 'PENDING_APPROVAL';
                        const isActive = row.status === 'ACTIVE';
                        const isApproved = row.status === 'APPROVED';
                        const isAutoApprovedRow = isApproved && row.approvedByUserId === row.userCreatedId;
                        const canEditRow = (isPending && (canEditAll || isOwner)) || (isApproved && (canEditAll || isOwner)) || (isActive && canWrite && (canEditAll || isOwner));
                        const canRemoveRow = (isPending && isOwner) || (isActive && canWrite && (canEditAll || isOwner)) || (isAutoApprovedRow && isOwner);
                        const canCancelApprovalRow = isApproved && canApprove && row.approvedByUserId === userProfile?.id && row.approvedByUserId !== row.userCreatedId;

                        return (
                            <div onClick={e => e.stopPropagation()}>
                                {!(isPending && canEditRow) && (
                                    <DropdownItem onClick={() => { setOpenRequestActionsDropdown(null); history.push(`/reservations/request-details/${row.id}`); }}>
                                        {t('common.details')}
                                    </DropdownItem>
                                )}
                                {canEditRow && (
                                    <DropdownItem onClick={() => { setOpenRequestActionsDropdown(null); history.push(`/reservations/request-details/${row.id}?edit=true`); }}>
                                        {t('common.edit')}
                                    </DropdownItem>
                                )}
                                {canCancelApprovalRow && (
                                    <DropdownItem onClick={() => { setOpenRequestActionsDropdown(null); showCancelApprovalDialog(row); }}>
                                        {t('requests.cancel_approval')}
                                    </DropdownItem>
                                )}
                                {canRemoveRow && (
                                    <DropdownItem onClick={() => { setOpenRequestActionsDropdown(null); showRemoveItemDialog(row); }}>
                                        {t('common.remove')}
                                    </DropdownItem>
                                )}
                            </div>
                        );
                    }}
                >
                    {() => (
                        <div
                            style={{ cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setOpenRequestActionsDropdown(openRequestActionsDropdown === row.id ? null : row.id!); }}
                        >
                            <FaEllipsisV />
                        </div>
                    )}
                </Popover>
            </div>
        );
    };

    const requestsColumns = useMemo<DataTableColumn<VehicleReservationDto>[]>(() => [
        {
            field: 'createdDate' as keyof VehicleReservationDto,
            name: t('reservations.list.request_date') as string,
            orderColumn: 'vr.created_date',
            width: 170,
            renderCell: row => (
                <>{row.createdDate ? <><DateFormat value={row.createdDate} format={DATE_FORMAT_DEFAUT} /> {new Date(row.createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</> : '-'}</>
            ),
        },
        {
            field: 'startDate' as keyof VehicleReservationDto,
            name: t('reservations.list.start_date') as string,
            orderColumn: 'vr.start_date',
            width: 170,
            renderCell: row => (
                <>{row.startDate ? <><DateFormat value={row.startDate} format={DATE_FORMAT_DEFAUT} /> {new Date(row.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</> : '-'}</>
            ),
        },
        {
            field: 'endDate' as keyof VehicleReservationDto,
            name: t('reservations.list.end_date') as string,
            orderColumn: 'vr.end_date',
            width: 170,
            renderCell: row => (
                <>{row.endDate ? <><DateFormat value={row.endDate} format={DATE_FORMAT_DEFAUT} /> {new Date(row.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</> : '-'}</>
            ),
        },
        {
            field: 'vehiclePhotoUrl' as keyof VehicleReservationDto,
            name: t('common.image') as string,
            hideOrderButton: true,
            width: 75,
            hideOn: ['sm', 'md'],
            excludeFromExport: true,
            renderCell: row => {
                const photoUrl = row.vehiclePhotoUrl || row.requestedVehiclePhotoUrl;
                const hasVehicle = row.vehicleRegistrationNumber || row.requestedVehicleRegistrationNumber;
                if (!hasVehicle && !photoUrl) {
                    return <div style={{ textAlign: 'center', width: '5rem' }}>-</div>;
                }
                return (
                    <div className={styles.imageCell}>
                        <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                            <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: photoUrl ? 'url(' + photoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                        </div>
                    </div>
                );
            },
        },
        {
            field: 'vehicleRegistrationNumber' as keyof VehicleReservationDto,
            name: t('reservations.list.vehicle') as string,
            orderColumn: 'v.registration_number',
            width: 130,
            renderCell: row => {
                const regNumber = row.vehicleRegistrationNumber || row.requestedVehicleRegistrationNumber;
                return <div className={styles.bold}>{regNumber || '-'}</div>;
            },
        },
        {
            field: 'vehicleBrandName' as keyof VehicleReservationDto,
            name: t('reservations.brand') as string,
            orderColumn: 'vb.name',
            width: 130,
            renderCell: row => <>{row.vehicleBrandName || row.requestedVehicleBrandName || '-'}</>,
        },
        {
            field: 'vehicleModelName' as keyof VehicleReservationDto,
            name: t('reservations.model') as string,
            orderColumn: 'vm.name',
            width: 130,
            renderCell: row => <>{row.vehicleModelName || row.requestedVehicleModelName || '-'}</>,
        },
        {
            field: 'driverName' as keyof VehicleReservationDto,
            name: t('reservations.list.driver') as string,
            orderColumn: 'anu.real_name',
            width: 200,
            renderCell: row => (
                <AvatarWithText src={row.driverPhotoUrl} size="x2" hideAvatar={!row.driverName}>
                    <div className={styles.bold}>{row.driverName || '-'}</div>
                </AvatarWithText>
            ),
        },
        {
            field: 'localName' as keyof VehicleReservationDto,
            name: t('reservations.list.agency') as string,
            orderColumn: 'l.name',
            width: 150,
            renderCell: row => <>{row.localName || '-'}</>,
        },
        {
            field: 'reason' as keyof VehicleReservationDto,
            name: t('reservations.destination_reason') as string,
            orderColumn: 'vr.reason',
            width: 180,
            renderCell: row => (
                <div className={styles.reasonCell}>
                    {row.reason?.trim() || '-'}
                </div>
            ),
        },
        {
            field: 'status' as keyof VehicleReservationDto,
            name: t('reservations.list.status') as string,
            orderColumn: 'vr.status',
            width: 140,
            renderCell: row => (
                <Badge
                    text={getReservationStatusLabel(row.status, t)}
                    preset={getReservationStatusBadgePreset(row.status)}
                />
            ),
        },
    ], [t]);

    const handleOrderChange = (orderData: OrderData<ReservationsVehicleListItemDto>) => {
        setOrder(orderData);
        setVehicles([]);
        resetScrollState();
        setTotalItems(0);
        setOpenRows([]);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderColumn: orderData.orderColumn ?? orderData.field as string,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
        }));
    };

    const handleRequestsOrderChange = (orderData: OrderData<VehicleReservationDto>) => {
        setRequestsOrder(orderData);
        setRequests([]);
        setRequestsReachedEnd(false);
        setRequestsCriteria(prev => ({
            ...prev,
            page: 1,
            orderColumn: orderData.orderColumn ?? orderData.field as string,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
        }));
    };

    // --- Sub-rows data loading ---
    const getReservations = async (vehicle: ReservationsVehicleListItemDto, page: number) => {
        if (vehicle.id) {
            Loading.show();
            try {
                const reservationsCriteria = {
                    itemsPerPage,
                    page,
                    orderBy: 'desc',
                    orderColumn: 'vr.start_date',
                    vehicleId: vehicle.id,
                    startDate: filters.reservationStartDate,
                    endDate: filters.reservationEndDate,
                    driverId: filters.driverId,
                };

                const reservationsList = await VehicleReservationsService.getListByVehicleId(reservationsCriteria);

                setVehicles(vv => {
                    const tempVehicles = vv.map(v => {
                        if (v.id === vehicle.id) {
                            const reservations = page > 1 ? (v.reservations || []) : [];
                            return {
                                ...v,
                                reservations: [...reservations, ...reservationsList.items],
                                reservationsReachedEnd: (reservationsList.totalPages == 0 || reservationsList.currentPage == reservationsList.totalPages) ? true : false,
                            };
                        }
                        return v;
                    });
                    return tempVehicles;
                });

                Loading.hide();
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get reservations list`, error);
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    const getVerifications = async (reservation: VehicleReservationViewModel, page: number) => {
        if (reservation.id && reservation.vehicleId) {
            Loading.show();
            try {
                const verificationsCriteria = {
                    itemsPerPage,
                    page,
                    orderBy: 'desc',
                    orderColumn: 'v.verification_date',
                    vehicleId: reservation.vehicleId,
                    tripStartDate: reservation.startDate,
                    tripEndDate: reservation.endDate,
                    reservationId: reservation.id
                };

                const verificationsList = await VehicleReservationsService.getReservationTrips(verificationsCriteria);
                const resultItems = verificationsList?.items ?? [];
                setVehicles(vv => {
                    const tempVehicles = vv.map(v => {
                        if (v.id === reservation.vehicleId) {
                            const tempReservations = (v.reservations || []).map(vr => {
                                if (vr.id === reservation.id) {
                                    const verifications = page > 1 ? (vr.verifications || []) : [];
                                    return {
                                        ...vr,
                                        verifications: [...verifications, ...resultItems],
                                        verificationsReachedEnd: !verificationsList || (verificationsList.totalPages == 0 || verificationsList.currentPage == verificationsList.totalPages) ? true : false,
                                    };
                                }
                                return vr;
                            });
                            v.reservations = tempReservations;
                        }
                        return v;
                    });
                    return tempVehicles;
                });
                Loading.hide();
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get reservation verifications list`, error);
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    // --- Row expand/collapse ---
    const toggleRowOpen = (row: ReservationsVehicleListItemDto, forceOpen = false) => {
        if (!row.id) return;
        if (openRows.find(id => id === row.id) && !forceOpen) {
            setOpenRows(openRows.filter(id => id !== row.id));
        } else {
            if (!row.reservations || forceOpen) {
                getReservations(row, 1);
            }
            setOpenRows([...openRows, row.id]);
        }
    };

    const toggleSubRowOpen = (row: VehicleReservationViewModel, forceOpen = false) => {
        if (!row.id) return;
        if (openSubRows.find(id => id === row.id) && !forceOpen) {
            setOpenSubRows(openSubRows.filter(id => id !== row.id));
        } else {
            if (!row.verifications || forceOpen) {
                getVerifications(row, 1);
            }
            setOpenSubRows([...openSubRows, row.id]);
        }
    };

    const onSubSubRowClick = (row: ReservationTripItemViewModel, vehicleId: string) => {
        if (row.source === 'GPS') {
            const tripDate = moment(row.startDate).format('YYYY-MM-DD');
            history.push(`/vehicles/trip/details/${vehicleId}/gps/list/${tripDate}/${row.id}/none`);
        } else {
            history.push(`/verifications/details/${row.id}`);
        }
    };

    const onSubRowsLoadMore = (row: ReservationsVehicleListItemDto) => {
        const totalPages = Math.ceil((row.reservations || []).length / itemsPerPage);
        getReservations(row, totalPages + 1);
    };

    const canSubRowsLoadMore = (row: ReservationsVehicleListItemDto): boolean => {
        return !row.reservationsReachedEnd;
    };

    const onSubSubRowsLoadMore = (row: VehicleReservationViewModel) => {
        const totalPages = Math.ceil((row.verifications || []).length / itemsPerPage);
        getVerifications(row, totalPages + 1);
    };

    const canSubSubRowsLoadMore = (row: VehicleReservationViewModel): boolean => {
        return !row.verificationsReachedEnd;
    };

    const isRowExpanded = (row: ReservationsVehicleListItemDto) => {
        return Boolean(openRows.find(id => id === row.id));
    };

    const isSubRowExpanded = (row: VehicleReservationViewModel) => {
        return Boolean(openSubRows.find(id => id === row.id));
    };

    // --- Remove ---
    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }
        try {
            await VehicleReservationsService.remove(itemToRemove);
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            if (parentItemToRemove) {
                getReservations(parentItemToRemove, 1);
            }
            // Always refresh requests tab too
            setRequests([]);
            setRequestsPageLoaded(false);
            setRequestsCriteria(prev => ({ ...prev, page: 1 }));
            loadRequestCounts();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete reservation`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const showRemoveItemDialog = async (item: VehicleReservationDto, parent?: ReservationsVehicleListItemDto) => {
        setItemToRemove(item);
        if (parent) setParentItemToRemove(parent);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    // --- Cancel approval ---
    const showCancelApprovalDialog = (item: VehicleReservationDto) => {
        setItemToCancelApproval(item);
        setShowCancelApprovalModal(true);
    };

    const onCancelApproval = async () => {
        if (!itemToCancelApproval?.id) return;
        try {
            Loading.show();
            await VehicleReservationsService.cancelApproval(itemToCancelApproval.id);
            Loading.hide();
            setShowCancelApprovalModal(false);
            setItemToCancelApproval(null);
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            // Refresh requests tab
            setRequests([]);
            setRequestsPageLoaded(false);
            setRequestsCriteria(prev => ({ ...prev, page: 1 }));
            loadRequestCounts();
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't cancel approval`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    // --- Render cells ---
    const renderVehicleCell = (row: ReservationsVehicleListItemDto) => (
        <div className={styles.vehicleCell}>
            <div className={styles.imageCell}>
                <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                    <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.photoUrl ? 'url(' + row?.photoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                </div>
            </div>
            <div className={styles.description}>
                <div className={styles.bold}>{row.vehicleRegistrationNumber}</div>
            </div>
        </div>
    );

    const renderDateCell = (row: VehicleReservationViewModel) => (
        <div>
            <div>
                {row && row.startDate ?
                    <span>
                        <span className={styles.bold}>{t('reservations.list.date_start')}:</span> <DateFormat value={row.startDate} format={DATE_FORMAT_DEFAUT} />
                    </span>
                    : '-'}
                {row && row.startDate && <span> {new Date(row.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
            </div>
            <div>
                {row && row.endDate ?
                    <span>
                        <span className={styles.bold}>{t('reservations.list.date_end')}:</span> <DateFormat value={row.endDate} format={DATE_FORMAT_DEFAUT} />
                    </span>
                    : '-'}
                {row && row.endDate && <span> {new Date(row.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} </span>}
            </div>
        </div>
    );

    const renderTripleRows = (row1: any, row2: any, row3: any) => (
        <div className={styles.flex}>
            {row1}
            {row2}
            {row3}
            {!row1 && !row2 && !row3 && '-'}
        </div>
    );

    const formatTotalMinutes = (time: number) => {
        const hours = Math.floor(time / 60);
        const minutes = Math.round(time % 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}  h`;
    };

    const renderVerificationDates = (row: ReservationTripItemViewModel) => {
        const dateBegin = row.startDate ? moment(row.startDate).toDate() : null;
        if (dateBegin) {
            if (row.startHour && row.startHour.length > 0) {
                const splittedHoursMinutesStart = row.startHour.split(':');
                dateBegin.setHours(Number(splittedHoursMinutesStart[0] ?? 0));
                dateBegin.setMinutes(Number(splittedHoursMinutesStart[1] ?? 0));
            }
        }

        const dateEnd = row.endDate ? moment(row.endDate).toDate() : null;
        if (dateEnd && row.endHour) {
            const splittedHoursMinutesEnd = row.endHour.split(':');
            dateEnd.setHours(Number(splittedHoursMinutesEnd[0] ?? 0));
            dateEnd.setMinutes(Number(splittedHoursMinutesEnd[1] ?? 0));
        }

        return (
            <>
                {row.source === 'GPS' && (
                    <span className={styles.gpsBadge}>
                        {t('reservations.trip_source_gps') as string}
                    </span>
                )}
                {renderTripleRows(
                    <div data-tip={t('car_verification.list.kms_traveled_travel_duration_tooltip')} data-for={`row_1_${row.id}`}><IconWithText
                        icon={GaugeIcon}
                        value={row.totalKms != undefined ? row.totalKms : '-'}
                        color='black'
                        isNumeric={true}
                        suffix={`${t('car_verification.list.kms')} / ${formatTotalMinutes(row.totalMinutes ?? 0)}`}
                    />
                        <ReactTooltip id={`row_1_${row.id}`} />
                    </div>,
                    dateBegin ? <div data-tip={t('car_verification.list.start_travel_date_tooltip')} data-for={`row_2_${row.id}`}><IconWithText
                        icon={PlayIcon}
                        valueDate={dateBegin}
                        valueHour={row.startHour}
                        isDate={true}
                    /> <ReactTooltip id={`row_2_${row.id}`} /> </div> : null,
                    dateBegin && !dateEnd ?
                        <IconWithText
                            icon={HourGlassIcon}
                            value={t('car_verification.list.in_course')}
                            suffix={t('car_verification.list.kms')}
                            color={colors.defaultGreen}
                            className={styles.labelSmall}
                        />
                        : dateBegin && dateEnd ? <div data-tip={t('car_verification.list.end_travel_date_tooltip')} data-for={`row_4_${row.id}`}>
                            <IconWithText
                                icon={FlagBWIcon}
                                valueDate={dateEnd}
                                valueHour={row.endHour ?? null}
                                isDate={true}
                            />
                            <ReactTooltip id={`row_4_${row.id}`} /> </div>
                            : null)}
                {row.source === 'GPS' && (row.startAddress || row.endAddress) && (
                    <div className={styles.tripAddresses}>
                        {row.startAddress && <span>{row.startAddress}</span>}
                        {row.startAddress && row.endAddress && <span> → </span>}
                        {row.endAddress && <span>{row.endAddress}</span>}
                    </div>
                )}
            </>
        );
    };

    const renderDriverCell = (row: VehicleReservationViewModel | ReservationTripItemViewModel) => (
        <div>
            <div>
                <AvatarWithText src={row.driverPhotoUrl}
                    size="x2"
                    hideAvatar={!row.driverName}><div className={styles.bold}>{row.driverName || '-'}</div></AvatarWithText>
            </div>
            <div className={styles.driverEmail}>{row.driverEmail}</div>
        </div>
    );

    const renderReservationActionsCell = (row: VehicleReservationViewModel, parentRow: ReservationsVehicleListItemDto) => {
        const elem = document.getElementById('root');
        const isOwner = row ? row.userCreatedId === userProfile?.id : true;
        const canRemoveRes = hasWorkflow
            ? (canEditAll || canApprove)
            : (canEditAll || (canWrite && isOwner));

        return (
            <div style={{ position: 'relative' }}>
                <Popover
                    positions={['left', 'bottom']}
                    align={'start'}
                    reposition
                    containerParent={elem ? elem : document.getElementsByTagName('body')[0]}
                    isOpen={openActionsDropdown === row.id}
                    onClickOutside={() => setOpenActionsDropdown(null)}
                    content={() =>
                        <div onClick={e => e.stopPropagation()}>
                            <DropdownItem onClick={() => { setOpenActionsDropdown(null); history.push(`/reservations/details/${row.id}`); }}>
                                {t('common.details')}
                            </DropdownItem>
                            {canRemoveRes && <DropdownItem onClick={() => { setOpenActionsDropdown(null); showRemoveItemDialog(row, parentRow); }}>
                                {t('common.remove')}
                            </DropdownItem>}
                        </div>
                    }
                >
                    {() => (
                        <div
                            style={{ cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setOpenActionsDropdown(openActionsDropdown === row.id ? null : row.id!); }}
                        >
                            <FaEllipsisV />
                        </div>
                    )}
                </Popover>
            </div>
        );
    };

    const formateDate = (date: Date, dayOnly?: boolean) => {
        const d = dayjs(date);
        const now = dayjs();

        let result = d.format('D MMMM');
        if (d.get('year') !== now.get('year')) {
            result += ` ${d.format('YYYY')}`;
        }

        if (dayOnly) return result;

        result += ` ${t('reservations.list.at')} ${d.format('H')}`;
        if (d.get('minute') > 0) {
            result += `:${d.format('mm')}`;
        }
        result += 'h';
        return result;
    };

    const getAvailabilityText = (row: ReservationsVehicleListItemDto): string => {
        if (row.availability) return t('reservations.list.available') as string;
        if (!row.availabilities || row.availabilities.length === 0) return t('reservations.list.not_available') as string;
        return row.availabilities.map(a => {
            if (a.endDate) {
                if (dayjs(a.endDate).diff(a.startDate, 'minute') <= 1) return '';
                if (dayjs(a.startDate).isSame(a.endDate, 'day')) {
                    return t('reservations.list.available_at', { date: formateDate(new Date(a.startDate as string), true), hourFrom: moment(new Date(a.startDate as string)).format(TIME_FORMAT_DEFAULT), hourTo: moment(new Date(a.endDate as string)).format(TIME_FORMAT_DEFAULT) }) as string;
                }
                return t('reservations.list.available_between', { dateFrom: formateDate(new Date(a.startDate as string), true), hourFrom: moment(new Date(a.startDate as string)).format(TIME_FORMAT_DEFAULT), dateTo: formateDate(new Date(a.endDate as string), true), hourTo: moment(new Date(a.endDate as string)).format(TIME_FORMAT_DEFAULT) }) as string;
            }
            return t('reservations.list.available_from', { date: formateDate(new Date(a.startDate as string)) }) as string;
        }).filter(Boolean).join('; ');
    };

    const renderAvailability = (row: ReservationsVehicleListItemDto) => (
        <div>
            {row.availability && <Badge text={t('reservations.list.available') as string} preset="greenAmerica" />}
            {!row.availability && row.availabilities && row.availabilities.length === 0 && <Badge text={t('reservations.list.not_available') as string} preset="danger" />}
            {!row.availability && row.availabilities && row.availabilities.length > 0 && row.availabilities.map((a, i) => (
                <div key={i}>{
                    a.endDate
                        ?
                        (dayjs(a.endDate).diff(a.startDate, 'minute') <= 1) ? null
                            : dayjs(a.startDate).isSame(a.endDate, 'day')
                                ? t('reservations.list.available_at', { date: formateDate(new Date(a.startDate), true), hourFrom: moment(new Date(a.startDate)).format(TIME_FORMAT_DEFAULT), hourTo: moment(new Date(a.endDate)).format(TIME_FORMAT_DEFAULT) })
                                : t('reservations.list.available_between', { dateFrom: formateDate(new Date(a.startDate), true), hourFrom: moment(new Date(a.startDate)).format(TIME_FORMAT_DEFAULT), dateTo: formateDate(new Date(a.endDate), true), hourTo: moment(new Date(a.endDate)).format(TIME_FORMAT_DEFAULT) })
                        : t('reservations.list.available_from', { date: formateDate(new Date(a.startDate)) })
                }
                </div>
            ))}
        </div>
    );

    const renderLocalNames = (row: ReservationsVehicleListItemDto) => {
        if (!row.localNames) return <></>;
        const allNames = row.localNames.split(', ').filter(n => n.trim().length > 0);
        if (allNames.length === 0) return <></>;

        const maxVisible = 3;
        const visible = allNames.slice(0, maxVisible);
        const remaining = allNames.slice(maxVisible);
        const tooltipId = `res_vehicle_agencies_${row.id}`;

        return (
            <div
                className={styles.localNamesCell}
                data-tip={t('reservations.list.agencies_label') as string}
                data-for={tooltipId}
            >
                {visible.map((name, i) => (
                    <span key={`res_agency_${row.id}_${i}`} className={styles.localName}>{name}</span>
                ))}
                {remaining.length > 0 && (
                    <div className={styles.localNamesOverflow}>
                        <span className={styles.localNamesMoreBadge}>+{remaining.length}</span>
                        <div className={styles.localNamesTooltip}>
                            {remaining.map((name, i) => (
                                <div key={`res_agency_extra_${row.id}_${i}`}>{name}</div>
                            ))}
                        </div>
                    </div>
                )}
                <ReactTooltip id={tooltipId} effect="solid" />
            </div>
        );
    };

    // --- DataTable columns ---
    const dataTableColumns = useMemo<DataTableColumn<ReservationsVehicleListItemDto>[]>(() => [
        {
            field: 'photoUrl' as keyof ReservationsVehicleListItemDto,
            name: t('common.image') as string,
            hideOrderButton: true,
            width: 75,
            hideOn: ['sm', 'md'],
            excludeFromExport: true,
            renderCell: row => (
                <div className={styles.imageCell}>
                    <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                        <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.photoUrl ? 'url(' + row?.photoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                    </div>
                </div>
            ),
        },
        {
            field: 'vehicleRegistrationNumber' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.list.vehicle') as string,
            orderColumn: 'v.registration_number',
            width: 130,
            cellStyle: { overflow: 'visible' },
            exportValue: row => row.vehicleRegistrationNumber ?? '',
            renderCell: row => (
                <div>
                    <div className={styles.bold}>{row.vehicleRegistrationNumber || '-'}</div>
                    {renderLocalNames(row)}
                </div>
            ),
        },
        {
            field: 'vehicleBrandName' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.brand') as string,
            orderColumn: 'vb.name',
            width: 200,
            renderCell: row => <>{row.vehicleBrandName || '-'}</>,
        },
        {
            field: 'vehicleModelName' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.model') as string,
            orderColumn: 'vm.name',
            width: 200,
            renderCell: row => <>{row.vehicleModelName || '-'}</>,
        },
        {
            field: 'driverName' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.list.driver') as string,
            width: 250,
            hideOrderButton: true,
            excludeFromExport: true,
            renderCell: () => <></>,
        },
        {
            field: 'reason' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.list.reason') as string,
            width: 200,
            hideOrderButton: true,
            excludeFromExport: true,
            renderCell: () => <></>,
        },
        {
            field: 'availability' as keyof ReservationsVehicleListItemDto,
            name: <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ display: 'flex' }}>{t('reservations.list.availability')}</span>
                <span style={{ marginLeft: '0.5rem', cursor: 'pointer', color: colors.gray, display: 'flex' }} data-tip={t('reservations.list.availability_message')}><FaInfoCircle />
                    <ReactTooltip />
                </span>
            </div>,
            width: 300,
            hideOrderButton: true,
            hideOn: ['sm', 'md', 'lg'],
            excludeFromExport: true,
            renderCell: row => renderAvailability(row),
        },
    ], [t]);

    // --- Expandable row (uses renderExpandedCells for column alignment) ---
    const expandableRowConfig: ExpandableRowConfig<ReservationsVehicleListItemDto> = {
        isExpanded: row => isRowExpanded(row),
        onToggle: row => toggleRowOpen(row),
        renderContent: () => <></>,
        renderExpandedCells: vehicle => {
            const reservations = vehicle.reservations || [];
            const rows: { key: string; className?: string; chevronCell?: React.ReactElement; cells: Record<string, React.ReactElement | null>; colSpans?: Record<string, number> }[] = [];

            if (reservations.length === 0 && vehicle.reservationsReachedEnd !== undefined) {
                rows.push({
                    key: `no-res-${vehicle.id}`,
                    colSpans: { photoUrl: 7 },
                    cells: {
                        photoUrl: <div className={styles.noResults}>{t('reservations.without_reservations')}</div>,
                    },
                });
                return rows;
            }

            reservations.forEach((reservation, rIndex) => {
                const resClick = () => toggleSubRowOpen(reservation);
                rows.push({
                    key: `res-${reservation.id || rIndex}`,
                    className: styles.reservationExpandedRow,
                    colSpans: { photoUrl: 4 },
                    chevronCell: (
                        <span className={styles.subRowChevron} onClick={resClick}>
                            {isSubRowExpanded(reservation) ? <FaAngleDown /> : <FaAngleRight />}
                        </span>
                    ),
                    cells: {
                        photoUrl: (
                            <div className={styles.reservationHeader} onClick={resClick}>
                                {renderDateCell(reservation)}
                            </div>
                        ),
                        driverName: (
                            <div className={styles.expandedCellClick} onClick={resClick}>
                                {renderDriverCell(reservation)}
                            </div>
                        ),
                        reason: (
                            <div className={`${styles.expandedCellClick} ${styles.reasonCell}`} onClick={resClick}>
                                {reservation.reason || '-'}
                            </div>
                        ),
                        availability: (
                            <div className={styles.actionsRight} onClick={e => e.stopPropagation()}>
                                {renderReservationActionsCell(reservation, vehicle)}
                            </div>
                        ),
                    },
                });

                // --- Trip rows (when reservation is expanded) ---
                if (isSubRowExpanded(reservation)) {
                    const trips = reservation.verifications || [];

                    if (trips.length === 0 && reservation.verificationsReachedEnd !== undefined) {
                        rows.push({
                            key: `no-trips-${reservation.id}`,
                            colSpans: { photoUrl: 7 },
                            cells: {
                                photoUrl: <div className={styles.noResults}>{t('car_verification.list.without_trips')}</div>,
                            },
                        });
                    }

                    trips.forEach((v, vIndex) => {
                        const tripClick = () => onSubSubRowClick(v, vehicle.id);
                        rows.push({
                            key: `trip-${v.id || vIndex}`,
                            className: styles.tripExpandedRow,
                            colSpans: { photoUrl: 4 },
                            cells: {
                                photoUrl: (
                                    <div className={styles.expandedCellClick} onClick={tripClick}>
                                        {renderVerificationDates(v)}
                                    </div>
                                ),
                                driverName: (
                                    <div className={styles.expandedCellClick} onClick={tripClick}>
                                        {renderDriverCell(v)}
                                    </div>
                                ),
                                reason: null,
                                availability: null,
                            },
                        });
                    });

                    if (canSubSubRowsLoadMore(reservation)) {
                        rows.push({
                            key: `load-trips-${reservation.id}`,
                            colSpans: { photoUrl: 7 },
                            cells: {
                                photoUrl: (
                                    <div className={styles.loadMoreBtn} onClick={() => onSubSubRowsLoadMore(reservation)}>
                                        {t('common.load_more')}
                                    </div>
                                ),
                            },
                        });
                    }
                }
            });

            return rows;
        },
        renderExpandedFooter: vehicle => {
            if (!canSubRowsLoadMore(vehicle)) return null;
            return (
                <div className={styles.loadMoreBtn} onClick={() => onSubRowsLoadMore(vehicle)}>
                    {t('common.load_more')}
                </div>
            );
        },
    };

    if (type === 'new' || type === 'new-request') {
        const isRequestMode = type === 'new-request';
        return (
            <ScreenTitle title={t('reservations.title') as string}>
                <ScreenContainer>
                    <NewRequestScreen isRequestMode={isRequestMode} />
                </ScreenContainer>
            </ScreenTitle>
        );
    }

    if (type === 'details' || type === 'request-details') {
        const isRequestMode = type === 'request-details';
        return (
            <ScreenTitle title={t('reservations.title') as string}>
                <ScreenContainer>
                    <NewRequestScreen isRequestMode={isRequestMode} isDetailsMode={true} />
                </ScreenContainer>
            </ScreenTitle>
        );
    }

    return (
        <ScreenTitle title={t('reservations.title')}>
            <CompactPageLayout title={t('reservations.title') as string} contentAreaClassName={styles.contentArea}>
                {hasWorkflow ? (
                    <Tabs
                        compact
                        activeTabId={activeTab}
                        onChange={tabId => setActiveTab(tabId as 'reservations' | 'requests' | 'calendars')}
                        items={[
                            {
                                id: 'calendars',
                                title: t('reservations.tabs.calendars') as string,
                                content: (
                                    <div className={styles.tabContent}>
                                        <div className={`${compactPageLayoutStyles.rowCounters} ${styles.calendarCounters}`}>
                                            <InfoCard
                                                label={t('reservations.calendar.total_reservations') as string}
                                                value={calendarCounters.totalReservations}
                                                icon={CalendarIcon}
                                                backgroundColor={colors.counterTeal1}
                                            />
                                            <InfoCard
                                                label={t('reservations.calendar.total_requests') as string}
                                                value={calendarCounters.totalRequests}
                                                icon={HourGlassIcon}
                                                backgroundColor={colors.counterTeal2}
                                            />
                                            <InfoCard
                                                label={t('reservations.calendar.happening_now') as string}
                                                value={calendarCounters.happeningNow}
                                                icon={ClockIcon}
                                                backgroundColor={colors.counterBlue1}
                                            />
                                            <InfoCard
                                                label={t('reservations.calendar.upcoming') as string}
                                                value={calendarCounters.upcoming}
                                                icon={InfoIcon}
                                                backgroundColor={colors.counterBlue2}
                                                subtitle={t('reservations.calendar.upcoming_subtitle') as string}
                                            />
                                        </div>
                                        <div className={styles.tabHeader}>
                                            <div className={styles.tabHeaderLeft}>
                                                <div className={styles.viewToggle}>
                                                    <button
                                                        className={`${styles.viewToggleBtn} ${viewMode === 'month' ? styles.viewToggleBtnActive : ''}`}
                                                        onClick={() => setViewMode('month')}
                                                    >
                                                        <FaCalendarAlt /> {t('reservations.calendar.toggle_month')}
                                                    </button>
                                                    <button
                                                        className={`${styles.viewToggleBtn} ${viewMode === 'timeline' ? styles.viewToggleBtnActive : ''}`}
                                                        onClick={() => setViewMode('timeline')}
                                                    >
                                                        <FaStream /> {t('reservations.calendar.toggle_timeline')}
                                                    </button>
                                                </div>
                                                {viewMode === 'timeline' && (
                                                    <div className={styles.rangeToggle}>
                                                        {([7, 10, 30] as TimelineRange[]).map(range => (
                                                            <button
                                                                key={range}
                                                                className={`${styles.rangeToggleBtn} ${timelineRange === range ? styles.rangeToggleBtnActive : ''}`}
                                                                onClick={() => setTimelineRange(range)}
                                                            >
                                                                {t(`reservations.calendar.range_${range}_days` as any)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <FilterBadgesOverflow items={calendarFilterItems} align="left" />
                                            </div>
                                            <div className={styles.tabHeaderRight}>
                                                <div className={styles.dateNav}>
                                                    <button className={styles.dateNavToday} onClick={handleDateToday}>
                                                        {t('reservations.calendar.today')}
                                                    </button>
                                                    <span className={styles.dateNavTitle}>{dateNavTitle}</span>
                                                    <div className={styles.dateNavArrows}>
                                                        <button className={styles.dateNavBtn} onClick={handleDatePrev}>
                                                            <FaChevronLeft />
                                                        </button>
                                                        <button className={styles.dateNavBtn} onClick={handleDateNext}>
                                                            <FaChevronRight />
                                                        </button>
                                                    </div>
                                                </div>
                                                <Popover
                                                    contentContainerClassName={styles.filtersPopoverContainer}
                                                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff', overflow: 'visible' }}
                                                    positions={['bottom', 'left']}
                                                    align={'end'}
                                                    onClickOutside={() => updateCalendarFiltersTotal(calendarFilters)}
                                                    content={setIsPopoverOpen =>
                                                        <CalendarFilters
                                                            filters={calendarFilters}
                                                            onFilter={f => {
                                                                setIsPopoverOpen(false);
                                                                onChangeCalendarFilters(f);
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {(isPopoverOpen, setIsPopoverOpen) => (
                                                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                                            {calendarFiltersTotal > 0 &&
                                                                <div className={styles.counterList}> <div className={styles.counterNumber}>{calendarFiltersTotal}</div> </div>
                                                            }
                                                        </ScreenHeaderButton>
                                                    )}
                                                </Popover>
                                            </div>
                                        </div>
                                        {viewMode === 'month' ? (
                                            <CalendarView
                                                mode="combined"
                                                items={calendarCombinedItems}
                                                isLoading={calendarIsLoading}
                                                currentMonth={calendarMonth}
                                                selectedDate={calendarSelectedDate}
                                                onSelectDate={setCalendarSelectedDate}
                                            />
                                        ) : (
                                            <TimelineView
                                                mode="combined"
                                                items={timelineCombinedItems}
                                                allVehicles={allVehicles}
                                                isLoading={timelineIsLoading}
                                                startDate={timelineStart}
                                                daysCount={timelineRange}
                                                hasVehicleFilters={hasVehicleFilters}
                                            />
                                        )}
                                    </div>
                                ),
                            },
                            {
                                id: 'reservations',
                                title: t('reservations.tabs.reservations') as string,
                                content: (
                                    <div className={styles.tabContent}>
                                        <div className={styles.tabHeader}>
                                            <div className={styles.tabHeaderLeft}>
                                                <FilterBadgesOverflow items={filterItems} />
                                            </div>
                                            <div className={styles.tabHeaderRight}>
                                                <Popover
                                                    contentContainerClassName={styles.filtersPopoverContainer}
                                                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff', overflow: 'visible' }}
                                                    positions={['bottom', 'left']}
                                                    align={'end'}
                                                    onClickOutside={() => updateTotalFilters(filters)}
                                                    content={setIsPopoverOpen =>
                                                        <ReservationFilters
                                                            load={isSearch}
                                                            filters={filters}
                                                            mode="reservations"
                                                            onFilter={f => {
                                                                setIsPopoverOpen(false);
                                                                onChangeFilters(f);
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {(isPopoverOpen, setIsPopoverOpen) => (
                                                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                                            {filtersTotal > 0 &&
                                                                <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                                            }
                                                        </ScreenHeaderButton>
                                                    )}
                                                </Popover>
                                                <ExportListingButton exportRef={exportListingRef} />
                                            </div>
                                        </div>
                                        <div className={styles.alertMessage}>
                                            <span className={styles.iconAlertMessage}><FaInfoCircle /></span>
                                            <span>{t('reservations.only_show_vehicles_available_for_reservation')}</span>
                                        </div>
                                        <DataTableWithSidebar
                                            cacheKey="RESERVATIONS_TABLE"
                                            columns={dataTableColumns}
                                            rows={vehicles}
                                            defaultOrder={order}
                                            disallowHover={true}

                                            onOrderChange={handleOrderChange}
                                            onRowClick={row => toggleRowOpen(row)}
                                            onRowId={row => row.id ?? ''}
                                            expandableRow={expandableRowConfig}
                                            infiniteScroll={{
                                                isLoading: isLoadingMore,
                                                hasMore: getHasMore(vehicles.length, totalItems),
                                                onLoadMore: getOnLoadMore(loadMore),
                                                hasMorePrevious: getHasMorePrevious(),
                                                isLoadingPrevious,
                                                onLoadPrevious: getOnLoadPrevious(loadPrevious),
                                            }}
                                            exportRef={exportListingRef}
                                            hideExportInSidebar={true}
                                            exportExcel={{
                                                fetchAll: fetchAllForExport,
                                                fileName: t('reservations.title') as string,
                                                customExport: handleCustomExport,
                                            }}
                                        />
                                    </div>
                                ),
                            },
                            {
                                id: 'requests',
                                title: (
                                    <>
                                        {t('reservations.tabs.requests')}
                                        {canApprove && pendingRequestsCount > 0 && (
                                            <span className={styles.tabBadge} data-tip={t('reservations.tabs.pending_requests_tooltip' as any) as string} data-for="pending-requests-tooltip">
                                                {pendingRequestsCount}
                                                <ReactTooltip id="pending-requests-tooltip" />
                                            </span>
                                        )}
                                    </>
                                ),
                                content: (
                                    <div className={styles.tabContent}>
                                        <div className={compactPageLayoutStyles.rowCounters} style={{ padding: '0.75rem 0.75rem 0' }}>
                                            <InfoCard
                                                label={t('reservations.counters.total') as string}
                                                value={requestCounts.total}
                                                backgroundColor={colors.counterBlue2}
                                            />
                                            <InfoCard
                                                label={t('reservations.counters.approved') as string}
                                                value={requestCounts.approved}
                                                backgroundColor={colors.success}
                                            />
                                            <InfoCard
                                                label={t('reservations.counters.pending') as string}
                                                value={requestCounts.pending}
                                                backgroundColor={colors.warning}
                                            />
                                            <InfoCard
                                                label={t('reservations.counters.rejected') as string}
                                                value={requestCounts.rejected}
                                                backgroundColor={colors.danger}
                                            />
                                        </div>
                                        <div className={styles.tabHeader}>
                                            <div className={styles.tabHeaderLeft}>
                                                <FilterBadgesOverflow items={requestsFilterItems} />
                                            </div>
                                            <div className={styles.tabHeaderRight}>
                                                <Popover
                                                    contentContainerClassName={styles.filtersPopoverContainer}
                                                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff', overflow: 'visible' }}
                                                    positions={['bottom', 'left']}
                                                    align={'end'}
                                                    onClickOutside={() => updateRequestsFiltersTotal(requestsFilters)}
                                                    content={setIsPopoverOpen =>
                                                        <ReservationFilters
                                                            load={false}
                                                            filters={requestsFilters}
                                                            mode="requests"
                                                            onFilter={f => {
                                                                setIsPopoverOpen(false);
                                                                onChangeRequestsFilters(f);
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {(isPopoverOpen, setIsPopoverOpen) => (
                                                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                                            {requestsFiltersTotal > 0 &&
                                                                <div className={styles.counterList}> <div className={styles.counterNumber}>{requestsFiltersTotal}</div> </div>
                                                            }
                                                        </ScreenHeaderButton>
                                                    )}
                                                </Popover>
                                                <ExportListingButton exportRef={requestsExportRef} />
                                                {canWrite && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push('/reservations/new-request')} />}
                                            </div>
                                        </div>
                                        <DataTableWithSidebar
                                            cacheKey="REQUESTS_TABLE"
                                            columns={requestsColumns}
                                            rows={requests}
                                            defaultOrder={requestsOrder}
                                            disallowHover={true}
                                            onOrderChange={handleRequestsOrderChange}
                                            onRowClick={row => history.push(`/reservations/request-details/${row.id}`)}
                                            onRowId={row => row.id ?? ''}
                                            onRenderAction={row => renderRequestActionsCell(row)}
                                            infiniteScroll={{
                                                isLoading: requestsIsLoadingMore,
                                                hasMore: !requestsReachedEnd,
                                                onLoadMore: loadMoreRequests,
                                                hasMorePrevious: false,
                                                isLoadingPrevious: false,
                                                // eslint-disable-next-line @typescript-eslint/no-empty-function
                                                onLoadPrevious: () => { },
                                            }}
                                            exportRef={requestsExportRef}
                                            hideExportInSidebar={true}
                                            exportExcel={{
                                                fetchAll: fetchAllRequestsForExport,
                                                fileName: t('reservations.tabs.requests') as string,
                                                customExport: handleRequestsCustomExport,
                                            }}
                                        />
                                    </div>
                                ),
                            },
                        ] as TabItem[]}
                    />
                ) : (
                    <Tabs
                        compact
                        activeTabId={activeTab}
                        onChange={tabId => setActiveTab(tabId as 'reservations' | 'calendars')}
                        items={[
                            {
                                id: 'calendars',
                                title: t('reservations.tabs.calendars') as string,
                                content: (
                                    <div className={styles.tabContent}>
                                        <div className={`${compactPageLayoutStyles.rowCounters} ${styles.calendarCounters}`}>
                                            <InfoCard
                                                label={t('reservations.calendar.total_reservations') as string}
                                                value={calendarCounters.totalReservations}
                                                icon={CalendarIcon}
                                                backgroundColor={colors.counterTeal1}
                                            />
                                            <InfoCard
                                                label={t('reservations.calendar.happening_now') as string}
                                                value={calendarCounters.happeningNow}
                                                icon={ClockIcon}
                                                backgroundColor={colors.counterBlue1}
                                            />
                                            <InfoCard
                                                label={t('reservations.calendar.upcoming') as string}
                                                value={calendarCounters.upcoming}
                                                icon={InfoIcon}
                                                backgroundColor={colors.counterBlue2}
                                                subtitle={t('reservations.calendar.upcoming_subtitle') as string}
                                            />
                                        </div>
                                        <div className={styles.tabHeader}>
                                            <div className={styles.tabHeaderLeft}>
                                                <div className={styles.viewToggle}>
                                                    <button
                                                        className={`${styles.viewToggleBtn} ${viewMode === 'month' ? styles.viewToggleBtnActive : ''}`}
                                                        onClick={() => setViewMode('month')}
                                                    >
                                                        <FaCalendarAlt /> {t('reservations.calendar.toggle_month')}
                                                    </button>
                                                    <button
                                                        className={`${styles.viewToggleBtn} ${viewMode === 'timeline' ? styles.viewToggleBtnActive : ''}`}
                                                        onClick={() => setViewMode('timeline')}
                                                    >
                                                        <FaStream /> {t('reservations.calendar.toggle_timeline')}
                                                    </button>
                                                </div>
                                                {viewMode === 'timeline' && (
                                                    <div className={styles.rangeToggle}>
                                                        {([7, 10, 30] as TimelineRange[]).map(range => (
                                                            <button
                                                                key={range}
                                                                className={`${styles.rangeToggleBtn} ${timelineRange === range ? styles.rangeToggleBtnActive : ''}`}
                                                                onClick={() => setTimelineRange(range)}
                                                            >
                                                                {t(`reservations.calendar.range_${range}_days` as any)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <FilterBadgesOverflow items={calendarFilterItems} align="left" />
                                            </div>
                                            <div className={styles.tabHeaderRight}>
                                                <div className={styles.dateNav}>
                                                    <button className={styles.dateNavToday} onClick={handleDateToday}>
                                                        {t('reservations.calendar.today')}
                                                    </button>
                                                    <span className={styles.dateNavTitle}>{dateNavTitle}</span>
                                                    <div className={styles.dateNavArrows}>
                                                        <button className={styles.dateNavBtn} onClick={handleDatePrev}>
                                                            <FaChevronLeft />
                                                        </button>
                                                        <button className={styles.dateNavBtn} onClick={handleDateNext}>
                                                            <FaChevronRight />
                                                        </button>
                                                    </div>
                                                </div>
                                                <Popover
                                                    contentContainerClassName={styles.filtersPopoverContainer}
                                                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff', overflow: 'visible' }}
                                                    positions={['bottom', 'left']}
                                                    align={'end'}
                                                    onClickOutside={() => updateCalendarFiltersTotal(calendarFilters)}
                                                    content={setIsPopoverOpen =>
                                                        <CalendarFilters
                                                            filters={calendarFilters}
                                                            onFilter={f => {
                                                                setIsPopoverOpen(false);
                                                                onChangeCalendarFilters(f);
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {(isPopoverOpen, setIsPopoverOpen) => (
                                                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                                            {calendarFiltersTotal > 0 &&
                                                                <div className={styles.counterList}> <div className={styles.counterNumber}>{calendarFiltersTotal}</div> </div>
                                                            }
                                                        </ScreenHeaderButton>
                                                    )}
                                                </Popover>
                                            </div>
                                        </div>
                                        {viewMode === 'month' ? (
                                            <CalendarView
                                                mode="combined"
                                                items={calendarCombinedItems}
                                                isLoading={calendarIsLoading}
                                                currentMonth={calendarMonth}
                                                selectedDate={calendarSelectedDate}
                                                onSelectDate={setCalendarSelectedDate}
                                                hasWorkflow={false}
                                            />
                                        ) : (
                                            <TimelineView
                                                mode="combined"
                                                items={timelineCombinedItems}
                                                allVehicles={allVehicles}
                                                isLoading={timelineIsLoading}
                                                startDate={timelineStart}
                                                daysCount={timelineRange}
                                                hasVehicleFilters={hasVehicleFilters}
                                            />
                                        )}
                                    </div>
                                ),
                            },
                            {
                                id: 'reservations',
                                title: t('reservations.tabs.reservations') as string,
                                content: (
                                    <div className={styles.tabContent}>
                                        <div className={styles.tabHeader}>
                                            <div className={styles.tabHeaderLeft}>
                                                <FilterBadgesOverflow items={filterItems} />
                                            </div>
                                            <div className={styles.tabHeaderRight}>
                                                <Popover
                                                    contentContainerClassName={styles.filtersPopoverContainer}
                                                    containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff', overflow: 'visible' }}
                                                    positions={['bottom', 'left']}
                                                    align={'end'}
                                                    onClickOutside={() => updateTotalFilters(filters)}
                                                    content={setIsPopoverOpen =>
                                                        <ReservationFilters
                                                            load={isSearch}
                                                            filters={filters}
                                                            mode="reservations"
                                                            onFilter={f => {
                                                                setIsPopoverOpen(false);
                                                                onChangeFilters(f);
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {(isPopoverOpen, setIsPopoverOpen) => (
                                                        <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                                            {filtersTotal > 0 &&
                                                                <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                                            }
                                                        </ScreenHeaderButton>
                                                    )}
                                                </Popover>
                                                <ExportListingButton exportRef={exportListingRef} />
                                                {canWrite && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push('/reservations/new')} />}
                                            </div>
                                        </div>
                                        <div className={styles.alertMessage}>
                                            <span className={styles.iconAlertMessage}><FaInfoCircle /></span>
                                            <span>{t('reservations.only_show_vehicles_available_for_reservation')}</span>
                                        </div>
                                        <DataTableWithSidebar
                                            cacheKey="RESERVATIONS_TABLE"
                                            columns={dataTableColumns}
                                            rows={vehicles}
                                            defaultOrder={order}
                                            disallowHover={true}

                                            onOrderChange={handleOrderChange}
                                            onRowClick={row => toggleRowOpen(row)}
                                            onRowId={row => row.id ?? ''}
                                            expandableRow={expandableRowConfig}
                                            infiniteScroll={{
                                                isLoading: isLoadingMore,
                                                hasMore: getHasMore(vehicles.length, totalItems),
                                                onLoadMore: getOnLoadMore(loadMore),
                                                hasMorePrevious: getHasMorePrevious(),
                                                isLoadingPrevious,
                                                onLoadPrevious: getOnLoadPrevious(loadPrevious),
                                            }}
                                            exportRef={exportListingRef}
                                            hideExportInSidebar={true}
                                            exportExcel={{
                                                fetchAll: fetchAllForExport,
                                                fileName: t('reservations.title') as string,
                                                customExport: handleCustomExport,
                                            }}
                                        />
                                    </div>
                                ),
                            },
                        ] as TabItem[]}
                    />
                )}

                <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident')} />
                <QuestionYesNo onNo={() => { setShowCancelApprovalModal(false); setItemToCancelApproval(null); }} onYes={onCancelApproval} isVisible={showCancelApprovalModal} message={t('requests.cancel_approval_confirmation')} />
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default ReservationScreen;
